import type { NetworkInfo } from "@alch/dx-entities";
import type { ComposerMethodConfig } from "@features/composer/lib/methods";
import { skipToken, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { RawSchema } from "./lib/parser.js";
import type {
  ComposerParserWorkerMessage,
  ComposerParserWorkerResponse,
} from "./lib/parserWorker.js";
// eslint-disable-next-line import-x/extensions -- Vite worker query suffix, not a real extension
import ParserWorker from "./lib/parserWorker?worker&inline";

const parserWorker = new ParserWorker();

interface ComposerMetadata {
  files: string[];
  urls: string[];
}

interface UseComposerMethodConfigsProps {
  networkInfos: NetworkInfo[] | undefined;
  enabled?: boolean;
}

const useComposerMethodConfigs = ({
  networkInfos,
  enabled = true,
}: UseComposerMethodConfigsProps) => {
  const parsedQueryKey = useMemo(
    () => [
      "composer-methods-parsed",
      ...(networkInfos?.map((info) => info.id).sort() ?? []),
    ],
    [networkInfos],
  );

  const workerKey = useMemo(() => parsedQueryKey.join(), [parsedQueryKey]);

  const rawMethodsQuery = useQuery({
    queryKey: ["composer-methods"],
    staleTime: Infinity,
    enabled: networkInfos && networkInfos.length > 0 && enabled,
    queryFn: async () => {
      const response = await fetch(
        "https://dev-docs.alchemy.com/metadata.json",
      );

      if (!response.ok) {
        throw new Error("Failed to fetch sandbox metadata");
      }

      const { files, urls } = (await response.json()) as ComposerMetadata;

      const promises = await Promise.allSettled<RawSchema>(
        files.map(async (file) => {
          const response = await fetch(file);

          if (!response.ok) {
            throw new Error(`Failed to fetch API specs for ${file}`);
          }

          return { file, doc: (await response.json()) as RawSchema["doc"] };
        }),
      );

      const schemas = promises
        .filter((promise) => promise.status === "fulfilled")
        .map((promise) => promise.value);

      return { schemas, urls };
    },
  });

  const parsedMethodsQuery = useQuery({
    queryKey: parsedQueryKey,
    staleTime: Infinity,
    queryFn:
      rawMethodsQuery.data && networkInfos
        ? (): Promise<ComposerMethodConfig[]> => {
            const message: ComposerParserWorkerMessage = {
              key: workerKey,
              ...rawMethodsQuery.data,
              options: { networkInfos },
            };

            parserWorker.postMessage(message);

            return new Promise((resolve) => {
              const handler = (
                event: MessageEvent<ComposerParserWorkerResponse>,
              ) => {
                if (event.data.key === workerKey) {
                  resolve(event.data.result);
                  parserWorker.removeEventListener("message", handler);
                }
              };

              parserWorker.addEventListener("message", handler);
            });
          }
        : skipToken,
  });

  return {
    ...parsedMethodsQuery,
    isLoading: rawMethodsQuery.isLoading || parsedMethodsQuery.isLoading,
    isError: rawMethodsQuery.isError || parsedMethodsQuery.isError,
    error: rawMethodsQuery.error || parsedMethodsQuery.error,
  } as typeof parsedMethodsQuery;
};

export default useComposerMethodConfigs;
